import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useDraggable,
  MeasuringStrategy,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChoiceGroup,
  IconButton,
  TooltipHost,
  PrimaryButton,
  TextField,
  Dropdown,
  Checkbox,
} from "@fluentui/react";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import themeContext from "../../../../context/theme/ThemeContext";
import toastContext from "../../../../context/toast/ToastContext";
import { TOAST_TYPE, USERTYPES } from "../../../../constants/Constants";
import { getAdvKeyImagesAPI, updateAdvKeyImagesAPI } from "../../../../service/StudyViewerService";
import AuthenticatedImage from "./AuthenticatedImage";

const COLUMN_OPTIONS = [1, 2, 3];
const COLUMN_CHOICES = COLUMN_OPTIONS.map((n) => ({ key: String(n), text: String(n) }));
const LIBRARY_PREFIX = "lib::";
const PAGE_PREFIX = "page::";

// Hard cap on a key-image title — kept in sync with the backend column/validator:
// Images.advViewerKeyImageTitle is length=50 and KeyImageService.validateAdvanceViewerKeyImages
// rejects titles longer than 50. Enforced client-side so the user can't type a title that the
// save would silently reject.
const MAX_KEY_IMAGE_TITLE_LENGTH = 50;

// A captured page holds at most this many rows; capacity = columns * rows. Manual inserts onto a
// full page are rejected with a message (the user adds a new page instead) — kept in sync with the
// backend default-layout pagination (StudyReportCapturedImageService.CAPTURED_ROWS_PER_PAGE).
const CAPTURED_ROWS_PER_PAGE = 3;
const getPageCapacity = (columns) => (Number(columns) || 2) * CAPTURED_ROWS_PER_PAGE;

const buildSortableId = (templateKey, pageId, imageId) => `${templateKey}||${pageId}::${imageId}`;
const parseSortableId = (id) => {
  const [tplPart, rest] = String(id).split("||");
  if (!rest) return { templateKey: null, pageId: null, imageId: id };
  const sep = rest.indexOf("::");
  if (sep < 0) return { templateKey: tplPart, pageId: null, imageId: rest };
  return { templateKey: tplPart, pageId: rest.slice(0, sep), imageId: rest.slice(sep + 2) };
};

// Titles are generated server-side at ingestion (dicom-processor-service stamps
// "Image N" via a race-safe MAX(existing)+1 in the same transaction that persists
// the capture). The popup is a pure consumer of that title — it never invents one
// here, which previously let two rapid captures both become "Image 1" and tripped
// the backend's unique-title validation on save.
const getServerTitle = (item) => (item && item.title ? item.title : "");

const SortableImage = ({ templateKey, pageId, img, index, onRemove, onSelect, isPreviewSelected, t, centerInRow, title, showTitle, canEdit }) => {
  const sortableId = buildSortableId(templateKey, pageId, img.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    data: { type: "image", templateKey, pageId, imageId: img.id, image: img },
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    // A lone image in a row spans the whole row so it can be centered (matching the
    // report's gridSpan layout); the image itself is capped to one column's width via CSS.
    gridColumn: centerInRow ? "1 / -1" : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`captured-tab__cell ${isDragging ? "is-dragging" : ""} ${isPreviewSelected ? "is-preview-selected" : ""} ${centerInRow ? "captured-tab__cell--centered" : ""}`}
      {...(canEdit ? attributes : {})}
      {...(canEdit ? listeners : {})}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(img, templateKey, pageId);
      }}
    >
      {/* Inner wrapper hugs the displayed image so the title and remove button stay
          attached to the image — even when a lone image is centered at one-column width inside a
          full-row cell. */}
      <div className="captured-tab__cell-inner">
        <AuthenticatedImage url={img.previewUrl} alt={title || `capture-${img.id}`} className="captured-tab__image" />
        {showTitle && title ? (
          <div className="captured-tab__cell-title" title={title}>{title}</div>
        ) : null}
        {canEdit && (
          <div className="captured-tab__cell-actions">
            <TooltipHost content={t("captured-images-tab.remove-image")}>
              <IconButton
                iconProps={{ iconName: "Cancel" }}
                className="captured-tab__cell-action captured-tab__cell-action--danger"
                ariaLabel={t("captured-images-tab.remove-image")}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onRemove(templateKey, pageId, img.id); }}
              />
            </TooltipHost>
          </div>
        )}
      </div>
    </div>
  );
};

const PageDroppable = ({ templateKey, page, children, isSelected, onSelect }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${PAGE_PREFIX}${templateKey}||${page.id}`,
    data: { type: "page", templateKey, pageId: page.id },
  });

  return (
    <div
      ref={setNodeRef}
      className={`captured-page${isOver ? " is-cross-page-target" : ""}${isSelected ? " is-selected-target" : ""}`}
      onClick={onSelect}
      style={{ cursor: "pointer" }}
    >
      {children}
    </div>
  );
};

const LibraryItem = ({ item, title, isUsed, onToggleUsage, onPreview, t, canEdit }) => {
  const dragId = `${LIBRARY_PREFIX}${item.sopInstanceUid}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
    data: { type: "library", image: item },
    disabled: !canEdit,
  });

  return (
    <div
      ref={setNodeRef}
      className={`cti-lib-row${isDragging ? " is-dragging" : ""}${isUsed ? " is-used" : ""}`}
      onClick={() => onPreview(item)}
      {...(canEdit ? attributes : {})}
      {...(canEdit ? listeners : {})}
    >
      <label
        className="cti-lib-row__check"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={isUsed}
          disabled={!canEdit}
          onChange={() => onToggleUsage(item)}
          aria-label={t("captured-images-tab.library-select-item")}
        />
      </label>

      <div className="cti-lib-row__thumb">
        <AuthenticatedImage url={item.previewUrl} alt="sc-dicom" className="cti-lib-row__img" />
      </div>

      <div className="cti-lib-row__body">
        <div className="cti-lib-row__title" title={title}>{title}</div>
        <div className="cti-lib-row__meta">
          {!isUsed && (
            <span className="cti-lib-row__badge">{t("captured-images-tab.lib-unused") || "Available"}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Compact horizontal library card for the mobile "strip" layout (prototype design):
// a thumbnail with a corner ＋ quick-add, a green ✓ "in report" chip when used, and the
// title underneath. Draggable (type "library") so hold-drag onto the paper also works.
const LibraryStripItem = ({ item, title, isUsed, onQuickAdd, onPreview, t, canEdit }) => {
  const dragId = `${LIBRARY_PREFIX}${item.sopInstanceUid}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
    data: { type: "library", image: item },
    disabled: !canEdit,
  });
  return (
    <div className="cti-mstrip__item">
      <div
        ref={setNodeRef}
        className={`cti-mstrip__thumb${isDragging ? " is-dragging" : ""}${isUsed ? " is-used" : ""}`}
        onClick={() => onPreview(item)}
        {...(canEdit ? attributes : {})}
        {...(canEdit ? listeners : {})}
      >
        <AuthenticatedImage url={item.previewUrl} alt="sc-dicom" className="cti-mstrip__img" />
        {canEdit && (
          <button
            type="button"
            className="cti-mstrip__add"
            aria-label={t("captured-images-tab.library-select-item") || "Add"}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onQuickAdd(item); }}
          >
            &#xFF0B;
          </button>
        )}
        <span className={`cti-mstrip__used${isUsed ? " on" : ""}`} aria-hidden="true">&#x2713;</span>
      </div>
      <div className="cti-mstrip__title" title={title}>{title}</div>
    </div>
  );
};

// Placeholder row for a freshly captured SC that is still being ingested into object storage
// (the acceptor pipeline is async). Shows a spinner until it appears in the library, or a
// retry affordance if ingestion did not complete within the polling window.
const LibraryPendingItem = ({ sop, timedOut, onRetry, t }) => (
  <div className={`cti-lib-row cti-lib-row--pending${timedOut ? " is-timed-out" : ""}`}>
    <div className="cti-lib-row__thumb">
      {timedOut ? (
        <div className="cti-lib-row__pending-icon" aria-hidden="true">!</div>
      ) : (
        <div className="cti-lib-row__spinner" aria-hidden="true" />
      )}
    </div>
    <div className="cti-lib-row__body">
      <div className="cti-lib-row__title">
        {timedOut
          ? (t("captured-images-tab.capture-timed-out") || "Still processing…")
          : (t("captured-images-tab.capture-processing") || "Processing capture…")}
      </div>
      <div className="cti-lib-row__meta">
        {timedOut ? (
          <button type="button" className="cti-lib-row__retry" onClick={() => onRetry(sop)}>
            {t("button.retry") || "Retry"}
          </button>
        ) : (
          <span className="cti-lib-row__badge">{t("loading") || "Loading…"}</span>
        )}
      </div>
    </div>
  </div>
);

const ReportColumn = ({
  report,
  pages,
  templateKey,
  isActive,
  selectedCapturedPageId,
  onSelectPage,
  onAddPage,
  onRemovePage,
  onSetColumns,
  onClearPage,
  onRemoveImage,
  onSelectPreview,
  previewSelectedImageId,
  titleResolver,
  showTitle,
  canEdit,
  isMobile,
  t,
}) => {
  // On mobile the page-header actions (add / clear / delete) collapse behind a
  // hamburger menu so the header fits on one row; only one page's menu is open at a time.
  const [menuOpenPageId, setMenuOpenPageId] = useState(null);
  return (
    <div className="cti-report-col">
      <div className="cti-report-col__header">
        <span className="cti-report-col__name" title={report?.text}>{report?.text || "Report"}</span>
      </div>

      <div className="cti-report-col__pages">
        {(pages || []).length === 0 ? (
          <>
            <PageDroppable
              templateKey={templateKey}
              page={{ id: "__empty__" }}
              isSelected={false}
              onSelect={() => {}}
            >
              <div className="captured-page__empty">
                {t("captured-images-tab.empty-report") || "Drop images here"}
              </div>
            </PageDroppable>
            {canEdit && (
              <button
                type="button"
                className="cti-report-col__add-page"
                onClick={() => onAddPage(templateKey, null)}
              >
                + {t("captured-images-tab.add-page-after-label") || "Add page"}
              </button>
            )}
          </>
        ) : pages.map((page, pageIdx) => {
          const sortableIds = (page.images || []).map((img) => buildSortableId(templateKey, page.id, img.id));
          const isSelected = selectedCapturedPageId === page.id;
          return (
            <PageDroppable
              key={page.id}
              templateKey={templateKey}
              page={page}
              isSelected={isSelected}
              onSelect={() => onSelectPage(page.id)}
            >
              <div className="captured-page__header">
                <div className="captured-page__title">
                  <span className="captured-page__num">
                    {t("captured-images-tab.page-label")} {pageIdx + 1}
                  </span>
                  {isSelected && <span className="captured-page__selected-badge">&#x2713;</span>}
                </div>

                <div className="captured-page__columns" onClick={(e) => e.stopPropagation()}>
                  <span className="captured-tab__label">{t("captured-images-tab.columns-label")}</span>
                  <ChoiceGroup
                    className="captured-page__col-choice"
                    selectedKey={String(page.columns)}
                    options={COLUMN_CHOICES}
                    disabled={!canEdit}
                    onChange={(_, opt) => opt && onSetColumns(templateKey, page.id, Number(opt.key))}
                    styles={{ flexContainer: { display: "flex", gap: 6 } }}
                  />
                </div>

                {canEdit && (isMobile ? (
                  // Mobile: collapse the actions behind a hamburger so the header stays one row.
                  <div
                    className="captured-page__menu-wrap"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconButton
                      iconProps={{ iconName: "GlobalNavButton" }}
                      className="captured-page__menu-btn"
                      ariaLabel={t("captured-images-tab.page-actions") || "Page actions"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenPageId((cur) => (cur === page.id ? null : page.id));
                      }}
                    />
                    {menuOpenPageId === page.id && (
                      <>
                        <div
                          className="captured-page__menu-backdrop"
                          onClick={(e) => { e.stopPropagation(); setMenuOpenPageId(null); }}
                          aria-hidden="true"
                        />
                        <div className="captured-page__menu" role="menu">
                          <button
                            type="button"
                            role="menuitem"
                            className="captured-page__menu-item"
                            onClick={(e) => { e.stopPropagation(); setMenuOpenPageId(null); onAddPage(templateKey, page.id); }}
                          >
                            <span className="captured-page__menu-ic">&#xFF0B;</span>
                            {t("captured-images-tab.add-page-after-label") || "Add page"}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="captured-page__menu-item"
                            onClick={(e) => { e.stopPropagation(); setMenuOpenPageId(null); onClearPage(templateKey, page); }}
                          >
                            <span className="captured-page__menu-ic">&#x2715;</span>
                            {t("captured-images-tab.clear-images") || "Clear page"}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="captured-page__menu-item captured-page__menu-item--danger"
                            disabled={pages.length <= 1}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (pages.length <= 1) return;
                              setMenuOpenPageId(null);
                              onRemovePage(templateKey, page.id);
                            }}
                          >
                            <span className="captured-page__menu-ic">&#x1F5D1;</span>
                            {t("captured-images-tab.remove-page") || "Delete page"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                <div className="captured-page__actions">
                  <TooltipHost content={t("captured-images-tab.add-page-after-title")}>
                    <IconButton
                      iconProps={{ iconName: "Add" }}
                      ariaLabel={t("captured-images-tab.add-page-after-label")}
                      onClick={(e) => { e.stopPropagation(); onAddPage(templateKey, page.id); }}
                    />
                  </TooltipHost>
                  <TooltipHost content={t("captured-images-tab.clear-images") || "Clear page"}>
                    <IconButton
                      iconProps={{ iconName: "Clear" }}
                      ariaLabel={t("captured-images-tab.clear-images") || "Clear page"}
                      onClick={(e) => { e.stopPropagation(); onClearPage(templateKey, page); }}
                    />
                  </TooltipHost>
                  <TooltipHost
                    content={
                      (pages.length <= 1)
                        ? (t("captured-images-tab.remove-page-disabled") || "At least one page is required")
                        : t("captured-images-tab.remove-page")
                    }
                  >
                    <IconButton
                      iconProps={{ iconName: "Delete" }}
                      className="captured-page__action--danger"
                      ariaLabel={t("captured-images-tab.remove-page")}
                      disabled={pages.length <= 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pages.length <= 1) return;
                        onRemovePage(templateKey, page.id);
                      }}
                    />
                  </TooltipHost>
                </div>
                ))}
              </div>

              <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
                <div className="captured-page__viewport">
                  <div className="captured-page__sheet">
                    {(page.images || []).length === 0 ? (
                      <div className="captured-page__empty">
                        {t("captured-images-tab.no-images-on-page") || "No images on this page yet."}
                      </div>
                    ) : (
                      (() => {
                        const cols = page.columns;
                        const total = page.images.length;
                        const rows = Math.ceil(total / cols);
                        return (
                          <div
                            className="captured-tab__grid"
                            style={{
                              gridTemplateColumns: `repeat(${cols}, 1fr)`,
                              "--cti-cols": cols,
                              "--cti-rows": rows,
                            }}
                          >
                            {page.images.map((img, index) => {
                              const row = Math.floor(index / cols);
                              const imagesInRow = Math.min(cols, total - row * cols);
                              // A row holding a single image (a page of one, or the last odd
                              // image) is centered across the row — matching the report layout.
                              const centerInRow = imagesInRow === 1 && cols > 1;
                              return (
                                <SortableImage
                                  key={img.id}
                                  templateKey={templateKey}
                                  pageId={page.id}
                                  img={img}
                                  index={index}
                                  onRemove={onRemoveImage}
                                  onSelect={onSelectPreview}
                                  isPreviewSelected={previewSelectedImageId === img.id}
                                  t={t}
                                  centerInRow={centerInRow}
                                  title={titleResolver(img.sopInstanceUid)}
                                  showTitle={showTitle}
                                  canEdit={canEdit}
                                />
                              );
                            })}
                          </div>
                        );
                      })()
                    )}
                  </div>
                </div>
              </SortableContext>
            </PageDroppable>
          );
        })}

        {canEdit && (pages || []).length > 0 && (
          <button
            type="button"
            className="cti-report-col__add-page"
            onClick={() => onAddPage(templateKey, pages[pages.length - 1].id)}
          >
            + {t("captured-images-tab.add-page-after-label") || "Add page"}
          </button>
        )}
      </div>
    </div>
  );
};

const PreviewPanel = ({
  selected,
  title,
  usage,
  patientReports,
  reportNameByKey,
  onRename,
  onLiveRename,
  onDelete,
  onRemoveFromPage,
  onAddToReportPage,
  canEdit,
  t,
}) => {
  const [draft, setDraft] = useState(title || "");
  const [targetReportKey, setTargetReportKey] = useState(null);
  const [targetPageIdx, setTargetPageIdx] = useState(-1);

  // Load the title into the draft only when a different image is selected. We intentionally
  // don't depend on `title` here: live edits feed back into `title` via onLiveRename, and
  // re-seeding the draft on every change would fight the user's typing (e.g. clearing the
  // field would snap back to the fallback "Image N").
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDraft(title || ""); }, [selected?.sopInstanceUid]);

  if (!selected) {
    return (
      <div className="cti-preview cti-preview--empty">
        <div className="cti-preview__empty-msg">
          {t("captured-images-tab.preview-empty") || "Select an image to preview, rename, or move it across reports."}
        </div>
      </div>
    );
  }

  const reportOptions = (patientReports || []).map((r) => ({ key: r.key, text: r.text }));

  return (
    <div className="cti-preview">
      <div className="cti-preview__image-wrap">
        <AuthenticatedImage url={selected.previewUrl} alt={title} className="cti-preview__image" />
      </div>

      <div className="cti-preview__details">
      <div className="cti-preview__section">
        <label className="cti-preview__label">{t("captured-images-tab.title-label") || "Title"}</label>
        <div className="cti-preview__title-row">
          <TextField
            value={draft}
            maxLength={MAX_KEY_IMAGE_TITLE_LENGTH}
            readOnly={!canEdit}
            disabled={!canEdit}
            onChange={(_, v) => {
              // Hard-cap the length so we never feed the backend a title it would reject. maxLength
              // guards typing/paste in most browsers; the slice is a belt-and-suspenders backstop.
              const next = (v || "").slice(0, MAX_KEY_IMAGE_TITLE_LENGTH);
              setDraft(next);
              // Reflect the edit live across the preview panel, library row, and page cells.
              if (onLiveRename && selected?.sopInstanceUid) onLiveRename(selected.sopInstanceUid, next);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const next = (draft || "").trim().slice(0, MAX_KEY_IMAGE_TITLE_LENGTH);
                if (next && next !== title) onRename(selected.sopInstanceUid, next);
              }
            }}
            description={
              draft.length >= MAX_KEY_IMAGE_TITLE_LENGTH
                ? (t("captured-images-tab.title-max-length") || `Maximum ${MAX_KEY_IMAGE_TITLE_LENGTH} characters`)
                : undefined
            }
            ariaLabel={t("captured-images-tab.title-label") || "Title"}
          />
        </div>
      </div>

      <div className="cti-preview__section">
        <label className="cti-preview__label">{t("captured-images-tab.used-in") || "Used in"}</label>
        {usage.length === 0 ? (
          <div className="cti-preview__usage-empty">{t("captured-images-tab.not-placed") || "Not placed on any report"}</div>
        ) : (
          <ul className="cti-preview__usage">
            {usage.map((u, i) => {
              const reportName =
                (reportNameByKey && reportNameByKey[u.templateKey])
                || (patientReports || []).find((r) => r.key === u.templateKey)?.text
                || t("captured-images-tab.unknown-report")
                || "Report";
              return (
                <li key={`${u.templateKey}-${u.pageId}-${i}`} className="cti-preview__usage-item">
                  <span className="cti-preview__usage-report" title={reportName}>{reportName}</span>
                  <span className="cti-preview__usage-sep">·</span>
                  <span>{t("captured-images-tab.page-label")} {u.pageIndex + 1}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      </div>

    </div>
  );
};

const CapturedImagesTab = ({ onClose } = {}) => {
  const {
    studyId,
    capturedPages,
    removeCapturedImageFromReport,
    reorderImageInCapturedPage,
    moveImageBetweenPages,
    addCapturedPage,
    removeCapturedPage,
    setCapturedPageColumns,
    addCapturedPageForTemplate,
    removeCapturedPageForTemplate,
    setCapturedPageColumnsForTemplate,
    removeCapturedImageForTemplate,
    selectedCapturedPageId,
    setSelectedCapturedPageId,
    patientReports,
    activeTabKey,
    addImageReferenceToTemplate,
    addAllImagesToTemplate,
    capturedPagesMapRef,
    scDicomLibrary: library,
    scDicomLibraryLoading: libraryLoading,
    setLibraryItemTitle,
    setLibraryItemTitleLocal,
    removeLibraryItem,
    getLibraryItemUsage,
    moveCapturedImageAcrossTemplates,
    autosaveScheduleVersion,
    refreshCapturedPagesFromServer,
    refreshScDicomLibrary,
    pendingCaptureSops,
    resolvePendingCapture,
    showKeyImageTitle,
    setShowKeyImageTitle,
    appendCapturedImagesAtEnd,
    setAppendCapturedImagesAtEnd,
    persistCapturedPages,
    setKeyImagePresentStudyList,
    userRole,
  } = useContext(studyReportContext);

  // Editing this popup is reserved for the radiologist / QC supervisor workflow. Every other role
  // (admin, firm-admin, typist, hospital users, etc.) gets a fully view-only modal: they can browse,
  // preview, and scroll, but every editing affordance — drag/drop, library checkboxes, add/remove/
  // clear, column & placement settings, rename, and Save — is disabled or hidden.
  const canEdit = userRole === USERTYPES.RADIOLOGIST || userRole === USERTYPES.SUPERVISOR;
  const { showToastv2 } = useContext(toastContext);
  // This modal renders through a Fluent <Modal> portal on <body>, outside the
  // [data-theme] root — so theme-aware CSS vars can't cascade in. Carry the theme in
  // as a modifier class instead (see .cti-shell / .cti-shell--light tokens in styles.css).
  const { themeName } = useContext(themeContext);
  const isLightTheme = themeName === "light";

  const [t] = useTranslation();
  const [isSaving, setIsSaving] = useState(false);

  const [activeDrag, setActiveDrag] = useState(null);
  const [librarySearch, setLibrarySearch] = useState("");
  const [previewSelected, setPreviewSelected] = useState(null); // library item
  const [pendingDeleteSop, setPendingDeleteSop] = useState(null);

  // --- Mobile layout: the desktop 3-pane shell collapses into a tabbed,
  // single-focus layout on phones (see .cti-shell--mobile in styles.css).
  // `mobileTab` switches the main area between Library and Reports; options and
  // the image preview become bottom sheets. Detected via matchMedia, not a CSS
  // media query, so the JSX can also branch (tab bar, sheet headers, scrims).
  const [isMobile, setIsMobile] = useState(false);
  const [mobileTab, setMobileTab] = useState("library");
  const [optionsOpen, setOptionsOpen] = useState(false);
  // Mobile redesign (prototype layout): the report currently shown in the paper preview,
  // and which page of it the pager is on. Kept in sync with reportsForView below.
  const [mReportKey, setMReportKey] = useState(null);
  const [mPageIdx, setMPageIdx] = useState(0);
  useEffect(() => {
    // Phones and tablets held in portrait get the stacked mobile layout. iPad portrait widths:
    // Mini/Air/11" Pro are 768–834px; the 12.9" Pro is 1024px. To catch the 12.9" without also
    // flipping 1024px-wide *laptops* to mobile, the 834–1024px band additionally requires a coarse
    // (touch) pointer. iPad landscape (1194px+) and mouse-driven laptops keep the 3-pane layout.
    const mq = window.matchMedia("(max-width: 834px), (max-width: 1024px) and (pointer: coarse)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);
  // Preview and Options sheets are mutually exclusive — selecting an image to
  // preview dismisses the options sheet.
  useEffect(() => {
    if (previewSelected) setOptionsOpen(false);
  }, [previewSelected]);

  // Title resolver — look up the server-assigned title by sopInstanceUid. The title is
  // generated once at ingestion (DPS) and never derived from list position, so it is
  // stable and guaranteed unique per study.
  const titleBySop = useMemo(() => {
    const m = new Map();
    (library || []).forEach((it) => {
      m.set(it.sopInstanceUid, getServerTitle(it));
    });
    return m;
  }, [library]);

  const resolveTitle = useCallback((sop) => titleBySop.get(sop) || "", [titleBySop]);

  // Build per-report pages snapshot for side-by-side rendering.
  // Active report uses live `capturedPages`; others read from capturedPagesMapRef snapshot keyed by autosaveScheduleVersion.
  const reportsForView = useMemo(() => {
    const list = (patientReports && patientReports.length > 0) ? patientReports : (activeTabKey ? [{ key: activeTabKey, text: t("captured-images-tab.current-report") || "Current report" }] : []);
    // "Append at end" mode (default): captured images are rendered once at the very end of the
    // report, not attributed to any individual report. Show a single end-of-report bucket. The
    // bucket is the FIRST report's layout — captured images default onto the first report, and the
    // backend appends every capture once at the end regardless. Untick the flag to switch to the
    // per-report distribution view (all report columns).
    const scoped = appendCapturedImagesAtEnd
      ? list.slice(0, 1).map((r) => ({ ...r, text: t("captured-images-tab.end-of-report") || "End of report" }))
      : list;
    return scoped.map((r) => {
      const isActive = r.key === activeTabKey;
      const pages = isActive ? capturedPages : (capturedPagesMapRef.current?.[r.key] || []);
      return { report: r, pages, isActive };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientReports, activeTabKey, capturedPages, autosaveScheduleVersion, appendCapturedImagesAtEnd]);

  const totalImages = useMemo(
    () => reportsForView.reduce((n, r) => n + r.pages.reduce((nn, p) => nn + (p.images?.length || 0), 0), 0),
    [reportsForView],
  );

  // The report every library add lands on by default. It must be a report the popup actually
  // RENDERS: in "End of report" mode the view collapses to the first report only, while
  // activeTabKey follows the report tab the user last opened — so after adding a second template
  // (which switches the active tab to it) `activeTabKey` names a bucket that isn't on screen, and
  // adds/selection would silently go there. Prefer the active report when it's visible, else the
  // first visible column.
  const primaryTemplateKey = useMemo(() => {
    if (reportsForView.some(({ report }) => report.key === activeTabKey)) return activeTabKey;
    return reportsForView[0]?.report?.key || activeTabKey;
  }, [reportsForView, activeTabKey]);

  // --- Mobile paper-preview derivations (prototype layout) ---
  // The report shown in the paper preview, and the current page within it.
  const mReport = useMemo(() => {
    if (!reportsForView.length) return null;
    return reportsForView.find((r) => r.report.key === mReportKey) || reportsForView[0];
  }, [reportsForView, mReportKey]);
  const mPages = mReport?.pages || [];
  const mSafePageIdx = Math.min(mPageIdx, Math.max(0, mPages.length - 1));
  const mPage = mPages[mSafePageIdx] || null;

  // Keep the selected report valid as reportsForView changes (report added/removed, or the
  // placement toggle collapses to a single "End of report" bucket).
  useEffect(() => {
    if (!reportsForView.length) return;
    if (!reportsForView.some((r) => r.report.key === mReportKey)) {
      setMReportKey(reportsForView[0].report.key);
      setMPageIdx(0);
    }
  }, [reportsForView, mReportKey]);

  // Mirror the paged selection into the shared selectedCapturedPageId so quick-add / "Add all"
  // land on the page the user is actually looking at on mobile.
  useEffect(() => {
    if (isMobile && mPage?.id && mPage.id !== selectedCapturedPageId) {
      setSelectedCapturedPageId(mPage.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, mPage?.id]);

  // Desktop drags on a 5px mouse move; touch uses a press-and-hold (matching the
  // "hold & drag" hint) so a quick swipe still scrolls the page/library strip and only a
  // deliberate hold starts a drag. A dedicated TouchSensor is required — PointerSensor alone
  // lets the browser reclaim the touch as a scroll gesture and cancels the drag on mobile.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const filteredLibrary = useMemo(() => {
    const q = librarySearch.trim().toLowerCase();
    if (!q) return library || [];
    return (library || []).filter((it) => (titleBySop.get(it.sopInstanceUid) || "").toLowerCase().includes(q));
  }, [library, librarySearch, titleBySop]);

  const usedSops = useMemo(() => {
    const s = new Set();
    reportsForView.forEach(({ pages }) => {
      pages.forEach((p) => (p.images || []).forEach((img) => img.sopInstanceUid && s.add(img.sopInstanceUid)));
    });
    return s;
  }, [reportsForView]);

  // --- Pending captures (freshly captured SCs still ingesting into object storage) ---
  const [timedOutSops, setTimedOutSops] = useState([]);

  // A capture is "ready" only once it appears in the library WITH a server-assigned
  // title. DPS stamps the title in the same transaction that persists the row, so a
  // titled row means ingestion fully completed; an untitled row (DPS lag / disabled)
  // keeps us polling instead of surfacing a nameless image that can't be saved.
  const readySopSet = useMemo(
    () => new Set(
      (library || [])
        .filter((it) => it.title && String(it.title).trim())
        .map((it) => it.sopInstanceUid),
    ),
    [library],
  );

  // Captures we're still waiting on: in the pending set, not yet ready, not timed out.
  const pendingNotReady = useMemo(
    () => (pendingCaptureSops || []).filter((sop) => !readySopSet.has(sop) && !timedOutSops.includes(sop)),
    [pendingCaptureSops, readySopSet, timedOutSops],
  );

  // On popup open: load the EFFECTIVE captured-image layout from the server — the exact layout the
  // report renderer embeds (the derived default when the user hasn't customized, or the saved
  // arrangement when they have) — plus the SC library, and display it verbatim. The default layout
  // is derived server-side, so the popup never mutates or auto-arranges anything here: what you see
  // is what prints, whether or not the popup was ever opened before.
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  // Set once the user changes the arrangement this session. While false, a newly ingested capture
  // can safely reload the effective layout (the default already includes it); once true, we leave
  // the in-progress edits alone (the backend appends new captures on save).
  const userEditedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.all([
        typeof refreshCapturedPagesFromServer === "function" ? refreshCapturedPagesFromServer() : null,
        typeof refreshScDicomLibrary === "function" ? refreshScDicomLibrary() : null,
      ]);
      if (cancelled) return;
      setLayoutLoaded(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No background polling: the library is loaded once when the popup opens (see the
  // effect above) to avoid hammering the API with periodic refreshes across many
  // concurrent users on production. Captures that are still ingesting show a spinner;
  // the user can refresh a straggler on demand via the row's Retry affordance.

  // When a freshly captured image finishes ingesting and appears in the library, surface it without
  // mutating the layout. If the user hasn't edited the arrangement this session, the server-derived
  // default already includes the new capture, so we reload the effective layout to show it; if they
  // have edits in progress, we leave them intact (the backend appends new captures on save).
  useEffect(() => {
    const arrived = (pendingCaptureSops || []).filter((sop) => readySopSet.has(sop));
    if (arrived.length === 0) return;
    if (!userEditedRef.current && typeof refreshCapturedPagesFromServer === "function") {
      refreshCapturedPagesFromServer();
    }
    arrived.forEach((sop) => {
      resolvePendingCapture(sop);
      setTimedOutSops((prev) => prev.filter((s) => s !== sop));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readySopSet, pendingCaptureSops]);

  const handleRetryPending = useCallback((sop) => {
    setTimedOutSops((prev) => prev.filter((s) => s !== sop));
    if (typeof refreshScDicomLibrary === "function") refreshScDicomLibrary();
  }, [refreshScDicomLibrary]);

  // Manual, on-demand library refresh. We no longer poll in the background (to spare the API
  // under concurrent production load), so this is how the user pulls in captures that finished
  // ingesting after the popup was opened. Also clears any timed-out flags so stragglers get a
  // fresh chance to appear.
  const handleRefreshLibrary = useCallback(() => {
    setTimedOutSops([]);
    if (typeof refreshScDicomLibrary === "function") refreshScDicomLibrary();
  }, [refreshScDicomLibrary]);

  // Ensure every rendered report column has at least one (droppable) page — but only after the
  // effective layout has loaded, so a blank page is never created during the server-refresh's
  // transient empty state. This lets the user drop images even when there are no captures yet.
  useEffect(() => {
    if (!layoutLoaded) return;
    // Seed only the columns the popup RENDERS. Seeding the active report unconditionally used to
    // mint a page inside a report that "End of report" mode doesn't show — and addCapturedPage
    // moves the selection onto that page, leaving the visible column with nothing selected.
    // The active column seeds into its live state; the others into the snapshot map, so every
    // column shows a consistent "Page 1" drop target instead of the bare "Drop images here".
    reportsForView.forEach(({ report, pages, isActive }) => {
      if ((pages || []).length > 0) return;
      if (isActive) addCapturedPage(null);
      else addCapturedPageForTemplate(report.key, null);
    });
  }, [layoutLoaded, capturedPages.length, reportsForView, addCapturedPage, addCapturedPageForTemplate]);

  // Keep the selected page pointing at a page that is on screen. Switching report tabs clears the
  // selection (and a stale id can survive a placement-mode switch), which left "Add all" / the
  // library checkboxes with no visible target to land on.
  useEffect(() => {
    if (!layoutLoaded || isMobile) return;
    const inView = reportsForView.some(({ pages }) =>
      (pages || []).some((p) => p.id === selectedCapturedPageId));
    if (inView) return;
    const owner = reportsForView.find(({ pages }) => (pages || []).length > 0);
    if (owner) setSelectedCapturedPageId(owner.pages[owner.pages.length - 1].id);
  }, [layoutLoaded, isMobile, reportsForView, selectedCapturedPageId, setSelectedCapturedPageId]);

  const handleDragStart = (event) => {
    const data = event.active?.data?.current;
    if (!data) return;
    if (data.type === "library") {
      setActiveDrag({ type: "library", image: data.image });
    } else if (data.type === "image") {
      setActiveDrag({ type: "image", ...data });
    }
  };

  // Resolve drops by what the cursor is over, not by nearest-corner distance. dnd-kit's
  // distance strategies (closestCorners/closestCenter) can't reliably target a report column
  // whose only drop zone is a single EMPTY page when a populated report sits right beside it:
  // the nearest image in the neighbouring column wins and the empty column never becomes the
  // drop target — so dragging into a second/empty report appears to "do nothing" or drops onto
  // the wrong report. pointerWithin picks whatever is literally under the pointer. When the
  // cursor is over an existing image we prefer that collision (so in-page reordering keeps its
  // insert index); otherwise we take the page (the empty-column case). Fall back to
  // rectIntersection then closestCorners so a drop released in a gap is never lost.
  const collisionDetectionStrategy = useCallback((dndArgs) => {
    const pointer = pointerWithin(dndArgs);
    const candidates = pointer.length > 0 ? pointer : rectIntersection(dndArgs);
    if (candidates.length > 0) {
      const image = candidates.find(
        (c) => c.data?.droppableContainer?.data?.current?.type === "image",
      );
      return [image || candidates[0]];
    }
    return closestCorners(dndArgs);
  }, []);

  const resolveDropTarget = (over) => {
    if (!over) return null;
    const overData = over.data?.current;
    if (overData?.type === "page") {
      return { templateKey: overData.templateKey, pageId: overData.pageId, toIndex: -1 };
    }
    if (overData?.type === "image") {
      const tplKey = overData.templateKey;
      const rep = reportsForView.find((r) => r.report.key === tplKey);
      const page = rep?.pages.find((p) => p.id === overData.pageId);
      if (!page) return null;
      const toIndex = page.images.findIndex((img) => img.id === overData.imageId);
      return { templateKey: tplKey, pageId: overData.pageId, toIndex };
    }
    return null;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDrag(null);
    if (!over) return;

    const activeData = active.data?.current;
    const target = resolveDropTarget(over);
    if (!target) return;

    if (activeData?.type === "library") {
      // Appending to a full page (dropping onto the page body, toIndex < 0) spills onto the
      // next/new page automatically. Only a positional insert (dropping onto a specific image
      // within a full page) is rejected, since it can't keep that slot without overflowing.
      if (
        target.pageId !== "__empty__"
        && target.toIndex >= 0
        && isPageFull(target.templateKey, target.pageId)
      ) {
        notifyPageFull();
        return;
      }
      userEditedRef.current = true;
      addImageReferenceToTemplate(target.templateKey, {
        sopInstanceUid: activeData.image.sopInstanceUid,
        scImageId: activeData.image.scImageId,
        previewUrl: activeData.image.previewUrl,
        targetPageId: target.pageId === "__empty__" ? null : target.pageId,
        targetIndex: target.toIndex,
      });
      // On desktop, opening the preview right after a drop lets the user rename/adjust the
      // just-added image. On mobile the preview is a full bottom sheet that covers the layout,
      // so don't auto-open it on drop — the user opens it deliberately by tapping the image.
      if (!isMobile) setPreviewSelected(activeData.image);
      return;
    }

    if (activeData?.type === "image") {
      const fromTplKey = activeData.templateKey;
      const fromPageId = activeData.pageId;
      // Moving an image into a DIFFERENT (full) page is rejected; reordering within the same page
      // doesn't change its count, so it's always allowed.
      const isSamePage = fromTplKey === target.templateKey && fromPageId === target.pageId;
      if (!isSamePage && target.pageId !== "__empty__" && isPageFull(target.templateKey, target.pageId)) {
        notifyPageFull();
        return;
      }
      userEditedRef.current = true;
      if (fromTplKey === target.templateKey) {
        // Same report — reorder or move between pages
        if (fromPageId === target.pageId) {
          const rep = reportsForView.find((r) => r.report.key === fromTplKey);
          const page = rep?.pages.find((p) => p.id === fromPageId);
          if (!page) return;
          const fromIndex = page.images.findIndex((img) => img.id === activeData.imageId);
          const toIndex = target.toIndex < 0 ? page.images.length - 1 : target.toIndex;
          if (toIndex !== fromIndex && fromTplKey === activeTabKey) {
            reorderImageInCapturedPage(fromPageId, fromIndex, toIndex);
          } else if (fromTplKey !== activeTabKey) {
            moveCapturedImageAcrossTemplates(fromTplKey, fromPageId, activeData.imageId, fromTplKey, target.pageId, target.toIndex);
          }
        } else if (fromTplKey === activeTabKey) {
          const rep = reportsForView.find((r) => r.report.key === fromTplKey);
          const page = rep?.pages.find((p) => p.id === fromPageId);
          if (!page) return;
          const fromIndex = page.images.findIndex((img) => img.id === activeData.imageId);
          moveImageBetweenPages(fromPageId, fromIndex, target.pageId, target.toIndex);
        } else {
          moveCapturedImageAcrossTemplates(fromTplKey, fromPageId, activeData.imageId, fromTplKey, target.pageId, target.toIndex);
        }
      } else {
        moveCapturedImageAcrossTemplates(fromTplKey, fromPageId, activeData.imageId, target.templateKey, target.pageId === "__empty__" ? null : target.pageId, target.toIndex);
      }
    }
  };

  // A page is "full" once it holds columns * 3 rows of images. Manual inserts beyond that are
  // rejected with a message rather than silently spilling onto a new page, so the page layout
  // stays predictable and matches what prints.
  const findPageInView = useCallback((templateKey, pageId) => {
    const rep = reportsForView.find((r) => r.report.key === templateKey);
    return rep?.pages.find((p) => p.id === pageId) || null;
  }, [reportsForView]);

  const isPageFull = useCallback((templateKey, pageId) => {
    const page = findPageInView(templateKey, pageId);
    if (!page) return false;
    return (page.images?.length || 0) >= getPageCapacity(page.columns);
  }, [findPageInView]);

  const notifyPageFull = useCallback(() => {
    showToastv2(
      TOAST_TYPE.INFO,
      t("captured-images-tab.page-full")
        || "This page is full — a page holds at most 3 rows. Add a new page (＋) or remove an image to make room.",
    );
  }, [showToastv2, t]);

  // Resolve where a library add should land: the report + page the user has selected, falling
  // back to the primary (rendered) report's last page. Shared by quick-add and "Add all".
  const resolveAddTarget = useCallback(() => {
    let targetTemplateKey = primaryTemplateKey;
    let targetPageId = selectedCapturedPageId || null;
    if (selectedCapturedPageId) {
      const owner = reportsForView.find(({ pages }) =>
        (pages || []).some((p) => p.id === selectedCapturedPageId),
      );
      // A selection that belongs to no rendered column (e.g. left over from another report tab)
      // must not drag the add into an off-screen report — fall back to the primary column's page.
      if (owner) targetTemplateKey = owner.report.key;
      else targetPageId = null;
    }
    if (!targetPageId) {
      const fallback = reportsForView.find((r) => r.report.key === targetTemplateKey);
      const pages = fallback?.pages || [];
      targetPageId = pages.length > 0 ? pages[pages.length - 1].id : null;
    }
    return { targetTemplateKey, targetPageId };
  }, [primaryTemplateKey, selectedCapturedPageId, reportsForView]);

  const handleQuickAddFromLibrary = (item) => {
    const { targetTemplateKey, targetPageId } = resolveAddTarget();
    // No full-page rejection here: appending to a full page spills onto the next/new page
    // automatically (see addImageReferenceToTemplate), so overflow captures auto-paginate.
    userEditedRef.current = true;
    addImageReferenceToTemplate(targetTemplateKey, {
      sopInstanceUid: item.sopInstanceUid,
      scImageId: item.scImageId,
      previewUrl: item.previewUrl,
      targetPageId,
      targetIndex: -1,
    });
  };

  // Library images not yet placed on any report page.
  const unaddedLibraryItems = useMemo(
    () => (library || []).filter((it) => !usedSops.has(it.sopInstanceUid)),
    [library, usedSops],
  );

  // Place every unadded capture onto the selected page (selected report + layout). The page is
  // filled to capacity, then overflow spills into fresh pages inserted right after it.
  const handleAddAll = () => {
    if (unaddedLibraryItems.length === 0) return;
    userEditedRef.current = true;
    const { targetTemplateKey, targetPageId } = resolveAddTarget();
    if (!targetTemplateKey) return;
    addAllImagesToTemplate(
      targetTemplateKey,
      unaddedLibraryItems.map((it) => ({
        sopInstanceUid: it.sopInstanceUid,
        scImageId: it.scImageId,
        previewUrl: it.previewUrl,
      })),
      targetPageId,
    );
  };

  const handleRemoveImageEverywhere = (templateKey, pageId, imageId) => {
    userEditedRef.current = true;
    removeCapturedImageForTemplate(templateKey, imageId);
  };

  // Checkbox in the library row reflects whether the image is placed on any page.
  // Ticking adds it to the active page (same target resolution as quick-add);
  // unticking removes every instance of it across all reports/pages.
  const handleToggleLibraryUsage = (item) => {
    userEditedRef.current = true;
    const sop = item.sopInstanceUid;
    if (!usedSops.has(sop)) {
      handleQuickAddFromLibrary(item);
      return;
    }
    reportsForView.forEach(({ report, pages }) => {
      (pages || []).forEach((page) => {
        (page.images || []).forEach((img) => {
          if (img.sopInstanceUid === sop) {
            removeCapturedImageForTemplate(report.key, img.id);
          }
        });
      });
    });
  };

  const handleClearPage = (templateKey, page) => {
    (page.images || []).forEach((img) => handleRemoveImageEverywhere(templateKey, page.id, img.id));
  };

  const handleAddPageInReport = (templateKey, afterPageId) => {
    userEditedRef.current = true;
    addCapturedPageForTemplate(templateKey, afterPageId);
  };

  const handleRemovePageInReport = (templateKey, pageId) => {
    userEditedRef.current = true;
    removeCapturedPageForTemplate(templateKey, pageId);
  };

  const handleSetColumnsInReport = (templateKey, pageId, columns) => {
    userEditedRef.current = true;
    setCapturedPageColumnsForTemplate(templateKey, pageId, columns);
  };

  const handleDeleteLibraryItem = (item) => {
    setPendingDeleteSop(item.sopInstanceUid);
  };

  const confirmDeleteLibraryItem = () => {
    if (pendingDeleteSop) removeLibraryItem(pendingDeleteSop);
    if (previewSelected?.sopInstanceUid === pendingDeleteSop) setPreviewSelected(null);
    setPendingDeleteSop(null);
  };

  const previewUsage = useMemo(
    () => (previewSelected ? getLibraryItemUsage(previewSelected.sopInstanceUid) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewSelected, autosaveScheduleVersion, capturedPages],
  );

  const handlePreviewAddToReport = (item, templateKey) => {
    // Appending to a full last page spills onto a new page automatically (see
    // addImageReferenceToTemplate), so no full-page rejection is needed here.
    userEditedRef.current = true;
    addImageReferenceToTemplate(templateKey, {
      sopInstanceUid: item.sopInstanceUid,
      scImageId: item.scImageId,
      previewUrl: item.previewUrl,
    });
  };

  const activeImage = activeDrag?.image;

  // Horizontal scroll indicators for the reports workspace.
  const workspaceScrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false, hiddenRight: 0, hiddenLeft: 0 });

  const updateScrollState = useCallback(() => {
    const el = workspaceScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    const left = scrollLeft > 4;
    const right = scrollLeft < maxScroll - 4;
    // Approximate hidden report count using first column's width.
    const firstCol = el.querySelector(".cti-report-col");
    const colW = firstCol ? firstCol.getBoundingClientRect().width + 12 : 0;
    const hiddenRight = colW > 0 ? Math.max(0, Math.round((maxScroll - scrollLeft) / colW)) : 0;
    const hiddenLeft = colW > 0 ? Math.max(0, Math.round(scrollLeft / colW)) : 0;
    setScrollState({ left, right, hiddenRight, hiddenLeft });
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = workspaceScrollRef.current;
    if (!el) return undefined;
    const onScroll = () => updateScrollState();
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateScrollState());
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [updateScrollState, reportsForView.length]);

  // Sync the new popup's selection state with the legacy Advanced Viewer key-images
  // store: items present on any page -> isEnabled true, items removed -> isEnabled false.
  // Page contents themselves are persisted via the report autosave loop.
  const handleSaveAll = async () => {
    if (isSaving) return;
    if (!studyId) {
      if (typeof onClose === "function") onClose();
      return;
    }
    setIsSaving(true);
    try {
      // Source of truth for the adv-keyimages POST is the current server list —
      // preserves every field (seriesId, sopInstanceUid, imageURL, etc.) so the
      // backend validator does not reject our payload, and matches the shape the
      // legacy "Manage Key Images" popup sends.
      const advResponse = await getAdvKeyImagesAPI(studyId);
      const advList = Array.isArray(advResponse?.data?.data) ? advResponse.data.data : [];

      // Map imageId -> isUsed (placed on any report page) and imageId -> latest title.
      const usedImageIds = new Set();
      const titleByImageId = new Map();
      (library || []).forEach((it) => {
        if (it && it.imageId != null) {
          if (usedSops.has(it.sopInstanceUid)) usedImageIds.add(it.imageId);
          const tt = resolveTitle(it.sopInstanceUid) || it.title;
          if (tt) titleByImageId.set(it.imageId, tt);
        }
      });

      // Enabled state mirrors page placement 1:1. The previous 6-image cap was removed
      // (the backend no longer limits enabled key images) so the "in report or not" flag
      // is a single source of truth shared with the Manage Key Images popup.
      const payload = advList.map((row) => {
        const next = { ...row };
        next.enabled = usedImageIds.has(row.imageId);
        if (titleByImageId.has(row.imageId)) {
          next.title = titleByImageId.get(row.imageId);
        }
        return next;
      });

      const response = await updateAdvKeyImagesAPI(studyId, payload);
      if (response?.data?.result === "FAILURE") {
        showToastv2(TOAST_TYPE.ERROR, response.data.message || t("captured-images-tab.save-failed") || "Failed to save key images");
        return;
      }
      // Persist the captured-image page/layout structure (study_report captured tables) and
      // the per-study "show key image title" flag in a single call — without touching report
      // content (that is owned by the report save/submit flow).
      const pagesResponse = await persistCapturedPages(showKeyImageTitle, appendCapturedImagesAtEnd);
      if (pagesResponse?.result === "FAILURE") {
        showToastv2(TOAST_TYPE.ERROR, pagesResponse.message || t("captured-images-tab.save-failed") || "Failed to save key images");
        return;
      }
      // Bridge back to the Preview routing flag. The Preview button picks the
      // /preview/pdf/pdfwithkeyimage endpoint only when keyImagePresentStudyList is true,
      // but that flag is otherwise only set from the stale LoadStudy response. Without this,
      // captured images saved here would not appear in the preview until a page reload.
      if (typeof setKeyImagePresentStudyList === "function" && payload.some((row) => row.enabled)) {
        setKeyImagePresentStudyList(true);
      }
      showToastv2(TOAST_TYPE.SUCCESS, t("captured-images-tab.save-success") || "Layout saved");
      if (typeof onClose === "function") onClose();
    } catch (e) {
      console.error("Failed to sync adv key images", e);
      showToastv2(TOAST_TYPE.ERROR, t("captured-images-tab.save-failed") || "Failed to save key images");
    } finally {
      setIsSaving(false);
    }
  };

  const scrollWorkspace = (dir) => {
    const el = workspaceScrollRef.current;
    if (!el) return;
    const firstCol = el.querySelector(".cti-report-col");
    const step = firstCol ? firstCol.getBoundingClientRect().width + 12 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      // Every remove/deselect bumps autosaveScheduleVersion, which re-renders the popup and
      // re-flows the page grid. With dnd-kit's default WhileDragging measuring, droppable rects
      // are cached at drag-start and go stale after that layout shift, so collisions resolve to
      // nothing and drops silently no-op ("drag stops working after you remove an image"). Always
      // re-measure so drop targets track the current DOM.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveDrag(null)}
    >
      {isMobile ? (
        <div className={`cti-mshell${isLightTheme ? " cti-mshell--light" : ""}`}>
          {/* Top: report selector (multi-report) + options gear. With a single report there is
              nothing to switch between, so show its name as a static label instead of a dropdown.
              The old ‹ › pager is gone — pages now stack vertically and scroll (see below). */}
          <div className="cti-mtop">
            {reportsForView.length > 1 ? (
              <Dropdown
                className="cti-mtop__report"
                selectedKey={mReport?.report.key}
                options={reportsForView.map(({ report }) => ({ key: report.key, text: report.text }))}
                onChange={(_, opt) => { if (opt) { setMReportKey(opt.key); setMPageIdx(0); } }}
                ariaLabel={t("captured-images-tab.reports-tab") || "Report"}
              />
            ) : (
              <div className="cti-mtop__report-name" title={mReport?.report.text || ""}>
                {mReport?.report.text || ""}
              </div>
            )}
            <button
              type="button"
              className="cti-mpager__gear"
              onClick={() => { setPreviewSelected(null); setOptionsOpen(true); }}
              aria-label={t("captured-images-tab.options") || "Options"}
            >&#x2699;</button>
          </div>

          {/* Paper — all pages of the selected report stacked vertically and scrolled (same model
              as desktop), so the next page is reached by scrolling down instead of a hidden pager.
              Reuses ReportColumn: per-page "Page N" headers, columns control, add/clear/delete
              actions (behind the mobile hamburger), and an "Add page" button after the last page.
              Its internal report-name header is hidden on mobile (the name is shown in cti-mtop). */}
          <div className="cti-mpaper-wrap">
            {mReport ? (
              <ReportColumn
                report={mReport.report}
                pages={mPages}
                templateKey={mReport.report.key}
                isActive={mReport.isActive}
                selectedCapturedPageId={selectedCapturedPageId}
                onSelectPage={setSelectedCapturedPageId}
                onAddPage={handleAddPageInReport}
                onRemovePage={handleRemovePageInReport}
                onSetColumns={handleSetColumnsInReport}
                onClearPage={handleClearPage}
                onRemoveImage={handleRemoveImageEverywhere}
                onSelectPreview={(img) => {
                  const libItem = (library || []).find((it) => it.sopInstanceUid === img.sopInstanceUid) || img;
                  setPreviewSelected(libItem);
                }}
                previewSelectedImageId={null}
                titleResolver={resolveTitle}
                showTitle={showKeyImageTitle}
                canEdit={canEdit}
                isMobile={isMobile}
                t={t}
              />
            ) : (
              <div className="cti-mpaper__empty">{t("captured-images-tab.no-reports") || "No reports available."}</div>
            )}
          </div>

          {/* Library strip */}
          <div className="cti-mstrip-wrap">
            <div className="cti-mstrip__head">
              <span className="cti-mstrip__head-label">
                {t("captured-images-tab.library-title") || "Library"}
                {canEdit ? ` — ${t("captured-images-tab.drag-hint-mobile-short") || "hold & drag up, or tap ＋"}` : ""}
              </span>
              {canEdit && unaddedLibraryItems.length > 0 && (
                <button type="button" className="cti-mstrip__addall" onClick={handleAddAll}>
                  &#xFF0B; {t("captured-images-tab.add-all") || "Add all"}
                </button>
              )}
            </div>
            <div className="cti-mstrip">
              {filteredLibrary.length === 0 ? (
                <div className="cti-mstrip__empty">
                  {(library || []).length === 0
                    ? (t("captured-images-tab.library-empty") || "No images")
                    : (t("captured-images-tab.no-search-results") || "No matches")}
                </div>
              ) : filteredLibrary.map((item) => (
                <LibraryStripItem
                  key={item.sopInstanceUid}
                  item={item}
                  title={resolveTitle(item.sopInstanceUid)}
                  isUsed={usedSops.has(item.sopInstanceUid)}
                  onQuickAdd={handleQuickAddFromLibrary}
                  onPreview={(it) => setPreviewSelected(it)}
                  canEdit={canEdit}
                  t={t}
                />
              ))}
            </div>
          </div>

          {/* Save */}
          {canEdit && (
            <div className="cti-mfooter">
              <PrimaryButton
                className="cti-mfooter__save"
                onClick={handleSaveAll}
                disabled={isSaving}
                text={isSaving ? (t("saving") || "Saving…") : (t("button.save") || "Save")}
              />
            </div>
          )}

          {/* Options bottom sheet */}
          {optionsOpen && (
            <>
              <div className="cti-sheet-backdrop" onClick={() => setOptionsOpen(false)} aria-hidden="true" />
              <div className="cti-msheet">
                <div className="cti-sheet__header">
                  <span>{t("captured-images-tab.options") || "Options"}</span>
                  <button type="button" className="cti-sheet__close" onClick={() => setOptionsOpen(false)} aria-label={t("button.close") || "Close"}>&#x2715;</button>
                </div>
                <Checkbox
                  className="cti-library__show-title"
                  label={t("captured-images-tab.show-key-image-title") || "Show key image title"}
                  checked={!!showKeyImageTitle}
                  disabled={!canEdit}
                  onChange={(_, checked) => setShowKeyImageTitle(!!checked)}
                />
                <div className="cti-settings__placement">
                  <span className="captured-tab__label">{t("captured-images-tab.placement-label") || "Placement"}</span>
                  <ChoiceGroup
                    selectedKey={appendCapturedImagesAtEnd ? "end" : "per"}
                    options={[
                      { key: "end", text: t("captured-images-tab.placement-end") || "End of report" },
                      { key: "per", text: t("captured-images-tab.placement-per") || "Per report" },
                    ]}
                    disabled={!canEdit}
                    onChange={(_, opt) => opt && setAppendCapturedImagesAtEnd(opt.key === "end")}
                  />
                </div>
              </div>
            </>
          )}

          {/* Preview bottom sheet */}
          {previewSelected && (
            <>
              <div className="cti-sheet-backdrop" onClick={() => setPreviewSelected(null)} aria-hidden="true" />
              <div className="cti-msheet cti-msheet--preview">
                <div className="cti-sheet__header">
                  <span>{t("captured-images-tab.preview") || "Preview"}</span>
                  <button type="button" className="cti-sheet__close" onClick={() => setPreviewSelected(null)} aria-label={t("button.close") || "Close"}>&#x2715;</button>
                </div>
                <PreviewPanel
                  selected={previewSelected}
                  title={previewSelected ? resolveTitle(previewSelected.sopInstanceUid) : ""}
                  usage={previewUsage}
                  patientReports={patientReports}
                  reportNameByKey={reportsForView.reduce((acc, { report }) => { acc[report.key] = report.text; return acc; }, {})}
                  onRename={setLibraryItemTitle}
                  onLiveRename={setLibraryItemTitleLocal}
                  onDelete={handleDeleteLibraryItem}
                  onRemoveFromPage={() => {}}
                  onAddToReportPage={handlePreviewAddToReport}
                  canEdit={canEdit}
                  t={t}
                />
              </div>
            </>
          )}
        </div>
      ) : (
      <div
        className={`cti-shell${isLightTheme ? " cti-shell--light" : ""}${isMobile ? " cti-shell--mobile" : ""}`}
        data-mobile-tab={isMobile ? mobileTab : undefined}
        data-options-open={isMobile && optionsOpen ? "" : undefined}
        data-preview-open={isMobile && previewSelected ? "" : undefined}
      >

        {/* Mobile-only tab bar: Library | Reports + Options gear. */}
        {isMobile && (
          <div className="cti-mtabs" role="tablist">
            <button
              type="button"
              role="tab"
              className="cti-mtabs__tab"
              data-active={mobileTab === "library"}
              aria-selected={mobileTab === "library"}
              onClick={() => setMobileTab("library")}
            >
              {t("captured-images-tab.library-title") || "Library"}
              <span className="cti-mtabs__count">{(library || []).length}</span>
            </button>
            <button
              type="button"
              role="tab"
              className="cti-mtabs__tab"
              data-active={mobileTab === "reports"}
              aria-selected={mobileTab === "reports"}
              onClick={() => setMobileTab("reports")}
            >
              {t("captured-images-tab.reports-tab") || "Reports"}
            </button>
            <button
              type="button"
              className="cti-mtabs__gear"
              data-active={optionsOpen}
              aria-label={t("captured-images-tab.options") || "Options"}
              onClick={() => { setPreviewSelected(null); setOptionsOpen(true); }}
            >
              &#x2699;
            </button>
          </div>
        )}

        {/* LEFT — Library panel */}
        <aside className="cti-library">
          <div className="cti-library__panel">
          <div className="cti-library__header">
            <div className="cti-library__title-row">
              <div className="cti-library__title">
                {t("captured-images-tab.library-title") || "Library"}
                <span className="cti-library__count">{(library || []).length}</span>
              </div>
              <TooltipHost content={t("captured-images-tab.refresh-library") || "Refresh library"}>
                <IconButton
                  iconProps={{ iconName: "Refresh" }}
                  className="cti-library__refresh"
                  ariaLabel={t("captured-images-tab.refresh-library") || "Refresh library"}
                  onClick={handleRefreshLibrary}
                  disabled={libraryLoading}
                />
              </TooltipHost>
              <TooltipHost content={t("captured-images-tab.add-all-tooltip") || "Add all unadded captures to the selected page"}>
                <button
                  type="button"
                  className="cti-library__add-all"
                  onClick={handleAddAll}
                  disabled={!canEdit || unaddedLibraryItems.length === 0}
                >
                  + {t("captured-images-tab.add-all") || "Add all"}
                </button>
              </TooltipHost>
            </div>
            <TextField
              placeholder={t("captured-images-tab.search-placeholder") || "Search by title…"}
              value={librarySearch}
              onChange={(_, v) => setLibrarySearch(v || "")}
              className="cti-library__search"
            />
          </div>

          <div className="cti-library__list">
            {/* Pending captures still ingesting — only shown when not filtering by search. */}
            {!librarySearch.trim() && pendingNotReady.map((sop) => (
              <LibraryPendingItem key={`pending-${sop}`} sop={sop} timedOut={false} onRetry={handleRetryPending} t={t} />
            ))}
            {!librarySearch.trim() && timedOutSops.filter((sop) => !readySopSet.has(sop)).map((sop) => (
              <LibraryPendingItem key={`timedout-${sop}`} sop={sop} timedOut onRetry={handleRetryPending} t={t} />
            ))}
            {libraryLoading && <div className="cti-library__empty">{t("loading") || "Loading…"}</div>}
            {!libraryLoading && filteredLibrary.length === 0 && pendingNotReady.length === 0 && (
              <div className="cti-library__empty">
                {(library || []).length === 0
                  ? t("captured-images-tab.library-empty")
                  : (t("captured-images-tab.no-search-results") || "No matches.")}
              </div>
            )}
            {!libraryLoading && filteredLibrary.map((item) => (
              <LibraryItem
                key={item.sopInstanceUid}
                item={item}
                title={resolveTitle(item.sopInstanceUid)}
                isUsed={usedSops.has(item.sopInstanceUid)}
                onToggleUsage={handleToggleLibraryUsage}
                onPreview={(it) => setPreviewSelected(it)}
                canEdit={canEdit}
                t={t}
              />
            ))}
          </div>
          </div>
        </aside>

        {/* CENTER — Reports workspace */}
        <section className="cti-workspace">
          {scrollState.left && (
            <>
              <div className="cti-workspace__edge cti-workspace__edge--left" aria-hidden="true" />
              <button
                type="button"
                className="cti-workspace__nav cti-workspace__nav--left"
                onClick={() => scrollWorkspace(-1)}
                aria-label={t("captured-images-tab.scroll-prev-reports") || "Scroll to previous reports"}
              >
                <span className="cti-workspace__nav-chevron">&#x2039;</span>
                {scrollState.hiddenLeft > 0 && (
                  <span className="cti-workspace__nav-count">{scrollState.hiddenLeft}</span>
                )}
              </button>
            </>
          )}
          {scrollState.right && (
            <>
              <div className="cti-workspace__edge cti-workspace__edge--right" aria-hidden="true" />
              <button
                type="button"
                className="cti-workspace__nav cti-workspace__nav--right"
                onClick={() => scrollWorkspace(1)}
                aria-label={t("captured-images-tab.scroll-next-reports") || "Scroll to next reports"}
              >
                {scrollState.hiddenRight > 0 && (
                  <span className="cti-workspace__nav-count">{scrollState.hiddenRight}</span>
                )}
                <span className="cti-workspace__nav-chevron">&#x203A;</span>
              </button>
            </>
          )}
          <div className="cti-workspace__scroll" ref={workspaceScrollRef}>
          {reportsForView.length === 0 ? (
            <div className="cti-workspace__empty">{t("captured-images-tab.no-reports") || "No reports available."}</div>
          ) : (
            <div className="cti-workspace__cols" data-count={reportsForView.length}>
              {reportsForView.map(({ report, pages, isActive }) => (
                <ReportColumn
                  key={report.key}
                  report={report}
                  pages={pages}
                  templateKey={report.key}
                  isActive={isActive}
                  selectedCapturedPageId={selectedCapturedPageId}
                  onSelectPage={setSelectedCapturedPageId}
                  onAddPage={handleAddPageInReport}
                  onRemovePage={handleRemovePageInReport}
                  onSetColumns={handleSetColumnsInReport}
                  onClearPage={handleClearPage}
                  onRemoveImage={handleRemoveImageEverywhere}
                  onSelectPreview={(img, tplKey) => {
                    const libItem = (library || []).find((it) => it.sopInstanceUid === img.sopInstanceUid)
                      || { sopInstanceUid: img.sopInstanceUid, scImageId: img.scImageId, previewUrl: img.previewUrl };
                    setPreviewSelected(libItem);
                  }}
                  previewSelectedImageId={null}
                  titleResolver={resolveTitle}
                  showTitle={showKeyImageTitle}
                  canEdit={canEdit}
                  isMobile={isMobile}
                  t={t}
                />
              ))}
            </div>
          )}
          </div>
        </section>

        {/* RIGHT — Preview panel */}
        <aside className="cti-preview-panel">
          <div className="cti-preview-panel__settings">
            {isMobile && (
              <div className="cti-sheet__header">
                <span>{t("captured-images-tab.options") || "Options"}</span>
                <button
                  type="button"
                  className="cti-sheet__close"
                  aria-label={t("button.close") || "Close"}
                  onClick={() => setOptionsOpen(false)}
                >
                  &#x2715;
                </button>
              </div>
            )}
            <Checkbox
              className="cti-library__show-title"
              label={t("captured-images-tab.show-key-image-title") || "Show key image title"}
              checked={!!showKeyImageTitle}
              disabled={!canEdit}
              onChange={(_, checked) => setShowKeyImageTitle(!!checked)}
            />
            <div className="cti-settings__placement">
              <span className="captured-tab__label">
                {t("captured-images-tab.placement-label") || "Placement"}
              </span>
              <ChoiceGroup
                className="cti-settings__placement-choice"
                selectedKey={appendCapturedImagesAtEnd ? "end" : "per"}
                options={[
                  { key: "end", text: t("captured-images-tab.placement-end") || "End of report" },
                  { key: "per", text: t("captured-images-tab.placement-per") || "Per report" },
                ]}
                disabled={!canEdit}
                onChange={(_, opt) => opt && setAppendCapturedImagesAtEnd(opt.key === "end")}
                styles={{
                  flexContainer: {
                    display: "inline-flex",
                    gap: 2,
                    padding: 2,
                    borderRadius: 8,
                    border: "1px solid var(--cti-panel-border)",
                    background: "var(--cti-track-bg)",
                  },
                }}
              />
            </div>
          </div>
          <PreviewPanel
            selected={previewSelected}
            title={previewSelected ? resolveTitle(previewSelected.sopInstanceUid) : ""}
            usage={previewUsage}
            patientReports={patientReports}
            reportNameByKey={reportsForView.reduce((acc, { report }) => {
              acc[report.key] = report.text;
              return acc;
            }, {})}
            onRename={setLibraryItemTitle}
            onLiveRename={setLibraryItemTitleLocal}
            onDelete={handleDeleteLibraryItem}
            onRemoveFromPage={(u) => {
              // Find image id by sop in the given page and remove
              const tplKey = u.templateKey;
              const rep = reportsForView.find((r) => r.report.key === tplKey);
              const page = rep?.pages.find((p) => p.id === u.pageId);
              const img = page?.images.find((i) => i.sopInstanceUid === previewSelected?.sopInstanceUid);
              if (img) handleRemoveImageEverywhere(tplKey, page.id, img.id);
            }}
            onAddToReportPage={handlePreviewAddToReport}
            canEdit={canEdit}
            t={t}
          />
        </aside>

        <div className="cti-footer">
          <div className="cti-footer__left">
            {canEdit && (isMobile || totalImages > 0) && (
              <div className="cti-hint">
                {isMobile
                  ? t("captured-images-tab.drag-hint-mobile")
                  : t("captured-images-tab.drag-hint")}
              </div>
            )}
          </div>
          {canEdit && (
            <PrimaryButton
              className="cti-footer__save"
              onClick={handleSaveAll}
              disabled={isSaving}
              text={isSaving ? (t("saving") || "Saving…") : (t("button.save") || "Save")}
            />
          )}
        </div>

        {/* Mobile bottom-sheet scrims — tap outside to dismiss. */}
        {isMobile && optionsOpen && (
          <div className="cti-sheet-backdrop" onClick={() => setOptionsOpen(false)} aria-hidden="true" />
        )}
        {isMobile && previewSelected && (
          <div className="cti-sheet-backdrop" onClick={() => setPreviewSelected(null)} aria-hidden="true" />
        )}
      </div>
      )}

      <DragOverlay dropAnimation={null}>
        {activeImage ? (
          <div className="captured-tab__drag-overlay">
            <AuthenticatedImage url={activeImage.previewUrl} alt="dragging" className="captured-tab__image" />
          </div>
        ) : null}
      </DragOverlay>

      {pendingDeleteSop && (
        <div className="cti-confirm-backdrop" onClick={() => setPendingDeleteSop(null)}>
          <div className="cti-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="cti-confirm__title">{t("captured-images-tab.confirm-delete-title") || "Delete from library?"}</div>
            <div className="cti-confirm__body">
              {t("captured-images-tab.confirm-delete-body") || "This will remove the image from the library and from every report page where it appears."}
            </div>
            <div className="cti-confirm__actions">
              <PrimaryButton onClick={confirmDeleteLibraryItem} styles={{ root: { background: "#b3261e", borderColor: "#b3261e" } }}>
                {t("button.yes") || "Delete"}
              </PrimaryButton>
              <button type="button" className="cti-confirm__cancel" onClick={() => setPendingDeleteSop(null)}>
                {t("button.cancel") || "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DndContext>
  );
};

export default CapturedImagesTab;
